import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { notEq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { startOfDay, endOfDay } from 'date-fns'
import type { Log } from '@blakeelearning/log'
import type RouterService from '@ember/routing/router-service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { LogArgument } from '@blakeelearning/log/log-adapters/base'
import type Assignment from 'teacher-ui-client/models/assignment'
import AssignmentsAdminDateRange from 'teacher-ui-client/components/assignments-admin/date-range'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import { AssignmentExpireModal } from 'teacher-ui-client/components/assignments-admin/assignment-modal'
import UiButton from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type Owner from '@ember/owner'

interface Signature {
  Element: HTMLDivElement
  Args: {
    assignment: Assignment
    closeAssignment?: () => unknown
  }
}

export class EditAssignment extends Component<Signature> {
  @service log!: Log

  @service router!: RouterService

  @service subscriptionType!: SubscriptionTypeService

  @tracked isShowingModal = false

  @tracked newStartDate!: Date

  @tracked newDueDate!: Date

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.newStartDate = startOfDay(this.args.assignment.startAt)
    this.newDueDate = startOfDay(this.args.assignment.endAt)
  }

  @action
  endAssignmentModal() {
    this.isShowingModal = true
  }

  @action
  async endAssignment() {
    this.args.assignment.expiredAt = new Date()
    await this.args.assignment.save()
    await this.router.refresh('assignments.product.manage.current')

    this.isShowingModal = false
    this.args.closeAssignment?.()
  }

  @action
  async saveAssignment() {
    this.args.assignment.startAt = startOfDay(this.newStartDate)
    this.args.assignment.endAt = endOfDay(this.newDueDate)

    try {
      await this.args.assignment.save()
      await this.router.refresh('assignments.product.manage.current')
    } catch (error) {
      this.log.error('Updating assignment dates failed', error as LogArgument)
    }

    this.args.closeAssignment?.()
  }

  @action
  closeModal() {
    this.isShowingModal = false
  }

  @action
  updateDates({ startDate, endDate }: { startDate: Date; endDate?: Date | null }) {
    if (endDate) {
      this.newStartDate = startDate
      this.newDueDate = endDate
    }
  }

  <template>
    <div class="bg-juicy-orange-100 sticky left-0 space-y-3 px-7 py-4" data-test-edit-assignment-form ...attributes>
      <div class="font-bold">{{t "assignments.editDetails"}}</div>
      <div class="flex items-end">
        <AssignmentsAdminDateRange
          @startDateDisabled={{notEq @assignment.status "pending"}}
          @startDateDisabledText={{t "assignments.startDateDisabledTooltip"}}
          @startDate={{this.newStartDate}}
          @endDate={{this.newDueDate}}
          @onDateSelect={{this.updateDates}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />
      </div>
      <div class="grid grid-cols-[repeat(3,minmax(auto,1fr))] gap-2">
        <div class="justify-self-start">
          <UiButton data-test-expire-assignment {{on "click" this.endAssignmentModal}}>
            {{t "assignments.expireAssignment"}}
          </UiButton>
        </div>
        <div class="text-center">
          <UiButton
            data-test-save-changes
            @appearance={{ButtonAppearance.regular}}
            type="submit"
            {{on "click" this.saveAssignment}}
          >
            {{t "assignments.saveChanges"}}
          </UiButton>
        </div>
      </div>
    </div>

    <AssignmentExpireModal
      @assignmentName={{@assignment.title}}
      @showModal={{this.isShowingModal}}
      @closeModalAction={{this.closeModal}}
      @confirmAction={{this.endAssignment}}
      @rejectAction={{this.closeModal}}
    />
  </template>
}
